import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Spin, Typography, message } from 'antd';
import { ArrowLeftOutlined, CloseOutlined, MessageOutlined, SendOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';
import { useIsMobile } from '../../hooks/use_is_mobile';
import MessageBubble from '../Entities/MessageBubble';
import EmojiPicker, { insertAtCaret } from '../Entities/EmojiPicker';
import { dropDeleted, mergeMessages, replaceMessage, toggleReactionRows } from './channel_messages';
import '../Messages/ChatBubbles.css';
import './ChannelApp.css';

const { TextArea } = Input;
const { Text } = Typography;

const PAGE_LIMIT = 100;
const POLL_MS = 5000;

// The channel app (backend sdk_apps/channel.py): one conversation for every
// entity the instance is installed in. The backend serves pages of root
// messages and one thread's replies; verbs post, edit, delete and react, all
// as the signed-in person. This draws what it is given and keeps it fresh on
// a poll, like the built-in messages page, which it is meant to replace.
//
// A sent message shows at once (pending) and is swapped for the server's
// row when the verb returns; a failure takes it back out with a message.
function Composer({ value, onChange, onSend, placeholder, busy, inputRef, compact }) {
    const send = () => { if (value.trim() && !busy) onSend(value.trim()); };
    return (
        <div className="input-container channel-composer">
            <EmojiPicker disabled={busy} onSelect={emoji => insertAtCaret(inputRef, value, onChange, emoji)} />
            <TextArea
                ref={inputRef}
                value={value}
                onChange={e => onChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={placeholder}
                autoSize={{ minRows: 1, maxRows: 4 }}
                disabled={busy}
                className="message-input"
            />
            <Button type="primary" icon={<SendOutlined />} onClick={send} loading={busy} disabled={!value.trim()} className="send-button">
                {compact ? null : 'Send'}
            </Button>
        </div>
    );
}

function ChannelApp({ instance }) {
    const api = useApi();
    const { user } = useAuth();
    const isMobile = useIsMobile();
    const userId = user?.id;

    const [roots, setRoots] = useState(null);         // root messages, oldest first
    const [hasEarlier, setHasEarlier] = useState(false);
    const [loadingEarlier, setLoadingEarlier] = useState(false);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // The open thread: its root id and the replies fetched for it.
    const [threadId, setThreadId] = useState(null);
    const [replies, setReplies] = useState([]);
    const [replyDraft, setReplyDraft] = useState('');
    const [sendingReply, setSendingReply] = useState(false);

    const inputRef = useRef(null);
    const replyInputRef = useRef(null);
    const endRef = useRef(null);
    const threadEndRef = useRef(null);
    const rootsRef = useRef([]);
    useEffect(() => { rootsRef.current = roots || []; }, [roots]);

    const view = useCallback((params) => api.get(`/api/apps/${instance.id}/views/messages`, { params }).then(r => r.data), [api, instance.id]);
    const verb = useCallback((name, payload) => api.post(`/api/apps/${instance.id}/verbs/${name}`, payload).then(r => r.result), [api, instance.id]);

    // ── the newest page, then a poll that merges what others did ─────────────
    const fetchNewest = useCallback(async () => {
        const page = await view({ limit: PAGE_LIMIT });
        const held = rootsRef.current;
        let data = page.messages;
        // More than a page landed since the last poll: page forward from the
        // newest message held so nothing in between is lost.
        if (held.length && page.messages.length >= PAGE_LIMIT && !page.messages.some(m => held.some(h => h.message_id === m.message_id))) {
            let cursor = held[held.length - 1].message_id;
            for (let hops = 0; hops < 10 && cursor; hops++) {
                const more = await view({ after: cursor, limit: PAGE_LIMIT }).catch(() => null);
                if (!more || more.messages.length === 0) break;
                data = data.concat(more.messages);
                cursor = more.has_more ? more.messages[more.messages.length - 1].message_id : null;
            }
        }
        return { messages: data, has_more: page.has_more };
    }, [view]);

    useEffect(() => {
        let cancelled = false;
        setRoots(null);
        setError(null);
        view({ limit: PAGE_LIMIT })
            .then(page => { if (!cancelled) { setRoots(page.messages); setHasEarlier(page.has_more); } })
            .catch(err => { if (!cancelled) setError(err.status === 403 ? 'You cannot read this channel.' : 'Could not load this channel.'); });
        return () => { cancelled = true; };
    }, [view]);

    useEffect(() => {
        if (roots === null) return undefined;
        const tick = () => fetchNewest()
            .then(page => setRoots(prev => dropDeleted(mergeMessages(prev || [], page.messages), page.messages)))
            .catch(() => {});
        const timer = setInterval(tick, POLL_MS);
        return () => clearInterval(timer);
    }, [roots === null, fetchNewest]); // eslint-disable-line react-hooks/exhaustive-deps

    // The open thread follows the same poll.
    useEffect(() => {
        if (!threadId) { setReplies([]); return undefined; }
        let cancelled = false;
        const load = () => view({ thread: threadId, limit: PAGE_LIMIT })
            .then(page => { if (!cancelled) setReplies(prev => dropDeleted(mergeMessages(prev, page.messages), page.messages)); })
            .catch(err => { if (!cancelled && err.status === 404) setThreadId(null); });
        load();
        const timer = setInterval(load, POLL_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, [threadId, view]);

    // Only a new newest message pulls the view down; paging earlier history
    // in at the top, or a refreshed reaction, must not yank the reader away.
    const newestSeen = useRef(null);
    useEffect(() => {
        const newest = roots?.length ? roots[roots.length - 1].message_id : null;
        if (newest === newestSeen.current) return;
        newestSeen.current = newest;
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [roots]);
    useEffect(() => { threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [replies.length, threadId]);

    // ── verbs, each optimistic, the server's row then winning ─────────────────
    const loadEarlier = () => {
        if (!roots?.length) return;
        setLoadingEarlier(true);
        view({ before: roots[0].message_id, limit: PAGE_LIMIT })
            .then(page => {
                setRoots(prev => {
                    const have = new Set((prev || []).map(m => m.message_id));
                    return [...page.messages.filter(m => !have.has(m.message_id)), ...(prev || [])];
                });
                setHasEarlier(page.has_more);
            })
            .catch(() => message.error('Could not load earlier messages'))
            .finally(() => setLoadingEarlier(false));
    };

    const send = (content, replyTo, setList, setBusy, clear) => {
        const pending = {
            message_id: crypto.randomUUID(), content, timestamp: new Date().toISOString(), edited_at: null,
            sender_id: userId, sender_name: user?.name || user?.email || 'You', reply_to: replyTo,
            reactions: [], reply_count: 0, pending: true,
        };
        clear('');
        setBusy(true);
        setList(prev => [...(prev || []), pending]);
        if (replyTo) setRoots(prev => replaceMessage(prev || [], replyTo, m => ({ ...m, reply_count: (m.reply_count || 0) + 1 })));
        verb('post', { content, reply_to: replyTo, message_id: pending.message_id })
            .then(saved => setList(prev => replaceMessage(prev || [], pending.message_id, () => saved)))
            .catch(err => {
                setList(prev => (prev || []).filter(m => m.message_id !== pending.message_id));
                message.error(err.status === 403 ? 'You cannot post here' : (err.message || 'Could not send'));
            })
            .finally(() => setBusy(false));
    };

    const edit = (messageId, content, setList) => {
        setEditingId(null);
        if (!content) return;
        setList(prev => replaceMessage(prev || [], messageId, m => ({ ...m, content })));
        verb('edit', { message_id: messageId, content })
            .then(saved => setList(prev => replaceMessage(prev || [], messageId, () => saved)))
            .catch(err => message.error(err.status === 403 ? 'Only the writer or an admin can edit this' : 'Could not save the edit'));
    };

    const remove = (messageId, setList) => {
        setList(prev => (prev || []).filter(m => m.message_id !== messageId));
        if (threadId === messageId) setThreadId(null);
        verb('delete', { message_id: messageId })
            .catch(err => message.error(err.status === 403 ? 'Only the writer or an admin can delete this' : 'Could not delete'));
    };

    const toggleReaction = (messageId, emoji, setList) => {
        if (!userId) return;
        const held = [...(roots || []), ...replies].find(m => m.message_id === messageId);
        const mine = (held?.reactions || []).some(r => r.emoji === emoji && r.user_id === userId);
        setList(prev => replaceMessage(prev || [], messageId, m => ({ ...m, reactions: toggleReactionRows(m.reactions, userId, emoji) })));
        verb(mine ? 'unreact' : 'react', { message_id: messageId, emoji })
            .then(reactions => setList(prev => replaceMessage(prev || [], messageId, m => ({ ...m, reactions }))))
            .catch(() => message.error('Could not update reaction'));
    };

    const bubble = (msg, setList, extra = {}) => (
        <MessageBubble
            key={msg.message_id}
            msg={{ ...msg, role: 'user' }}
            own={msg.sender_id === userId}
            variantClass="assistant"
            editable
            isEditing={editingId === msg.message_id}
            onStartEdit={m => setEditingId(m.message_id)}
            onSaveEdit={(id, content) => edit(id, content, setList)}
            onCancelEdit={() => setEditingId(null)}
            onDelete={id => remove(id, setList)}
            currentUserId={userId}
            onToggleReaction={(id, emoji) => toggleReaction(id, emoji, setList)}
            {...extra}
        />
    );

    if (error) return <Text type="secondary">{error}</Text>;
    if (roots === null) return <Spin />;

    const root = threadId ? roots.find(m => m.message_id === threadId) : null;
    const showMain = !isMobile || !threadId;
    const showThread = !!threadId && !!root;

    return (
        <div className="channel-app">
            {showMain && (
                <div className="channel-main">
                    <div className="messages-container">
                        {roots.length === 0 ? (
                            <div className="empty-state">
                                <MessageOutlined style={{ fontSize: 28, opacity: 0.3 }} />
                                <Text type="secondary" style={{ marginTop: 12, fontSize: 13 }}>No messages yet. Say hello.</Text>
                            </div>
                        ) : (<>
                            {hasEarlier && (
                                <div style={{ textAlign: 'center', padding: '4px 0' }}>
                                    <Button type="link" size="small" loading={loadingEarlier} onClick={loadEarlier}>Load earlier messages</Button>
                                </div>
                            )}
                            {roots.map(msg => bubble(msg, setRoots, {
                                active: threadId === msg.message_id,
                                replyCount: msg.reply_count || 0,
                                onToggleThread: () => setThreadId(threadId === msg.message_id ? null : msg.message_id),
                            }))}
                        </>)}
                        <div ref={endRef} />
                    </div>
                    {!threadId && (
                        <Composer value={draft} onChange={setDraft} busy={sending} inputRef={inputRef}
                            placeholder="Message… (Enter to send)"
                            onSend={content => send(content, null, setRoots, setSending, setDraft)} />
                    )}
                </div>
            )}
            {showThread && (
                <div className="channel-thread">
                    <div className="channel-thread-head">
                        {isMobile && <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={() => setThreadId(null)} style={{ padding: 0 }} />}
                        <Text strong style={{ fontSize: 13, flex: 1 }}>Thread</Text>
                        {!isMobile && <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setThreadId(null)} style={{ padding: 0 }} />}
                    </div>
                    <div className="channel-thread-root">{bubble(root, setRoots)}</div>
                    <div className="messages-container">
                        {replies.length === 0 ? (
                            <div style={{ padding: '20px 12px', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 12 }}>No replies yet</Text></div>
                        ) : replies.map(msg => bubble(msg, setReplies))}
                        <div ref={threadEndRef} />
                    </div>
                    <Composer value={replyDraft} onChange={setReplyDraft} busy={sendingReply} inputRef={replyInputRef} compact
                        placeholder="Reply…"
                        onSend={content => send(content, threadId, setReplies, setSendingReply, setReplyDraft)} />
                </div>
            )}
        </div>
    );
}

export default ChannelApp;
